/**
 * LA VOIX — les réglages, et le calcul qui synchronise la page avec le son (design §3).
 *
 * TOUT CE QUI SE CALCULE EST ICI, ET C'EST PUR. Où en est la lecture après onze secondes, quel
 * feuillet montrer, à quelle vitesse lire : trois questions qui n'ont besoin ni d'un navigateur, ni
 * d'un élément `<audio>`, ni d'une voix installée. Le composant, lui, ne fait que brancher.
 *
 * LA SYNCHRONISATION SE FAIT AU PRORATA DES SIGNES, et il faut dire pourquoi c'est acceptable. La
 * bande du serveur couvre le nœud ENTIER (§7 : une bande par nœud, fabriquée une fois, servie à
 * tous) ; l'écran, lui, montre des feuillets. Pour tourner la page au bon moment, on convertit la
 * position d'un feuillet dans le texte en secondes, en supposant un débit constant. Ce n'est pas
 * exact au mot près — une réplique lente, une virgule tenue —, et ce n'est pas grave : l'erreur se
 * compte en fractions de seconde sur un feuillet de trois cents signes, et le pire qu'elle produise
 * est une page tournée un souffle trop tôt. L'autre solution — une bande par feuillet — casserait
 * le cache du §7, qui est ce qui rend la voix gratuite à partir du deuxième joueur.
 */
import { useCallback, useSyncExternalStore } from "react";

/** Cinq crans, comme la taille du texte. On règle une vitesse en tâtonnant, pas en choisissant
 * « 1,25 » dans une liste. */
export const VITESSES = [
  { clef: "lente", nom: "Lente", facteur: 0.8 },
  { clef: "posee", nom: "Posée", facteur: 0.9 },
  { clef: "normale", nom: "Normale", facteur: 1 },
  { clef: "vive", nom: "Vive", facteur: 1.15 },
  { clef: "pressee", nom: "Pressée", facteur: 1.35 }
] as const;

export type Vitesse = (typeof VITESSES)[number]["clef"];
export const VITESSE_PAR_DEFAUT: Vitesse = "normale";

export function vitesseValide(valeur: string | null | undefined): Vitesse {
  return (VITESSES.find((v) => v.clef === valeur)?.clef ?? VITESSE_PAR_DEFAUT) as Vitesse;
}

export function facteurDe(vitesse: Vitesse): number {
  return VITESSES.find((v) => v.clef === vitesse)?.facteur ?? 1;
}

export function vitesseVoisine(vitesse: Vitesse, sens: 1 | -1): Vitesse {
  const rang = VITESSES.findIndex((v) => v.clef === vitesse);
  return VITESSES[Math.min(VITESSES.length - 1, Math.max(0, rang + sens))]!.clef;
}

/**
 * L'INSTANT OÙ COMMENCE CHAQUE FEUILLET, en secondes, pour une bande qui couvre tout le texte lu.
 *
 * Le premier commence à zéro, toujours. Les suivants au prorata des signes déjà passés. On rend un
 * tableau de la même longueur que les feuillets : le composant n'a plus qu'à comparer.
 */
export function bornesDesFeuillets(feuillets: string[][], secondes: number): number[] {
  const longueurs = feuillets.map((feuillet) => feuillet.join(" ").length);
  const total = longueurs.reduce((somme, n) => somme + n, 0);
  if (total === 0 || secondes <= 0) return feuillets.map(() => 0);

  const bornes: number[] = [];
  let cumul = 0;
  for (const longueur of longueurs) {
    bornes.push((cumul / total) * secondes);
    cumul += longueur;
  }
  return bornes;
}

/** Le feuillet qu'on devrait avoir sous les yeux à cet instant-là. On ne revient JAMAIS en arrière :
 * une page qui recule parce qu'un `timeupdate` est arrivé dans le désordre est une page qui
 * clignote. */
export function feuilletPourLInstant(bornes: number[], seconde: number, page: number): number {
  let vise = page;
  for (let i = page + 1; i < bornes.length; i++) {
    if (seconde >= bornes[i]!) vise = i;
  }
  return vise;
}

/* --- Le réglage, écrit dans le navigateur du joueur --------------------------------- */

const CLEF_VITESSE = "destinia.vitesse";
const CLEF_MAINS_LIBRES = "destinia.mainsLibres";
const CHANGEMENT = "destinia:voix";

function lis(clef: string): string | null {
  try {
    return localStorage.getItem(clef);
  } catch {
    return null;
  }
}

function ecris(clef: string, valeur: string): void {
  try {
    localStorage.setItem(clef, valeur);
  } catch {
    /* stockage refusé : le réglage vivra le temps de la session, et c'est tout */
  }
  window.dispatchEvent(new Event(CHANGEMENT));
}

function abonne(rappel: () => void): () => void {
  window.addEventListener(CHANGEMENT, rappel);
  return () => window.removeEventListener(CHANGEMENT, rappel);
}

export function useVitesse(): [Vitesse, (v: Vitesse) => void] {
  const vitesse = useSyncExternalStore(abonne, () => vitesseValide(lis(CLEF_VITESSE)), () => VITESSE_PAR_DEFAUT);
  return [vitesse, useCallback((suivante: Vitesse) => ecris(CLEF_VITESSE, suivante), [])];
}

/**
 * LE MODE MAINS LIBRES. Il ne s'arme QUE parce qu'on l'a demandé (design §3 : c'est la seule
 * exception à « rien ne s'ouvre tout seul », et elle est explicite). Le réglage se garde d'une
 * session à l'autre — quelqu'un qui écoute écoute toujours.
 */
export function useMainsLibres(): [boolean, (arme: boolean) => void] {
  const arme = useSyncExternalStore(abonne, () => lis(CLEF_MAINS_LIBRES) === "1", () => false);
  return [arme, useCallback((suivant: boolean) => ecris(CLEF_MAINS_LIBRES, suivant ? "1" : "0"), [])];
}
